/**
 * A drop-in for `react-style-singleton` that never writes an inline `<style>` element.
 *
 * `react-remove-scroll-bar` — under every modal Radix Dialog (the drawers), Popover and Select —
 * locks the page by mounting `react-style-singleton`, and that package appends a
 * `<style>` element with the lock's rules (`body[data-scroll-locked] { overflow: hidden … }`) as
 * text. The edge's CSP is `style-src 'self'`, so Chromium blocks that element and logs a
 * violation each time a drawer opens (feedback triage, UI v0.100.1:
 * `sha256-nzTgYzXYDNe6BAHiiI7NNlfK8n/auuOAhh2t92YvuXo=` is that text with a zero scrollbar gap).
 *
 * The CSP stays as it is. `vite.config.ts` aliases the package to this module, which keeps its
 * three exports and their counting semantics (the rules arrive with the first user and leave with
 * the last) but hands the same text to a constructed stylesheet in `document.adoptedStyleSheets`.
 * A constructed stylesheet is CSSOM, not markup, and `style-src` does not govern it. Where the
 * browser has no constructable stylesheets the rules are simply not applied: the scroll lock still
 * holds (the library cancels wheel and touch scrolling itself), only the page scrollbar stays drawn.
 */
import { useEffect, type FC } from 'react';

type Stylesheet = { readonly add: (style: string) => void; readonly remove: () => void };

function adopt(css: string): CSSStyleSheet | null {
  if (typeof document === 'undefined' || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) return null;
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
    return sheet;
  } catch {
    return null;
  }
}

function release(sheet: CSSStyleSheet): void {
  if (!Array.isArray(document.adoptedStyleSheets)) return;
  document.adoptedStyleSheets = document.adoptedStyleSheets.filter((adopted) => adopted !== sheet);
}

export const stylesheetSingleton = (): Stylesheet => {
  let counter = 0;
  let sheet: CSSStyleSheet | null = null;
  return {
    add: (style) => {
      if (counter === 0) sheet = adopt(style);
      counter += 1;
    },
    remove: () => {
      counter -= 1;
      if (counter === 0 && sheet !== null) {
        release(sheet);
        sheet = null;
      }
    },
  };
};

export const styleHookSingleton = (): ((styles: string, isDynamic?: boolean) => void) => {
  const sheet = stylesheetSingleton();
  return (styles, isDynamic) => {
    // The library's own dependency list, kept: the rules are re-applied on a change of text only
    // when the caller marked them dynamic; otherwise the first text stays until the last user leaves.
    const key = isDynamic === true ? styles : false;
    useEffect(() => {
      sheet.add(styles);
      return () => { sheet.remove(); };
    }, [key]);
  };
};

export const styleSingleton = (): FC<{ styles: string; dynamic?: boolean }> => {
  const useStyle = styleHookSingleton();
  const Sheet: FC<{ styles: string; dynamic?: boolean }> = ({ styles, dynamic }) => {
    useStyle(styles, dynamic);
    return null;
  };
  return Sheet;
};
